import { useEffect, useMemo, useRef, useState } from 'react';
import type { SeriesPoint } from '../../src/api/queries';
import { TALLINN } from '../../src/wire';
import type { CoverageFact, FreezeReason, SeriesMetricKey } from './coverage';

/**
 * The live counter's arithmetic (ux-spec §3.2/§3.3), kept out of the component that draws it.
 * Everything a reader could be misled by is decided here: the counter is anchored to the day the
 * API says it covers rather than to the clock, it may only run at a pace measured from real recent
 * weeks, and it stops entirely when the number underneath it is not a year-to-date total.
 */

const MS_PER_DAY = 86_400_000;

/** The ordinary extraction lag (ux-spec §3.3). Past it the corpus has a backlog, not a delay. */
export const COUNTER_LAG_DAYS = 5;

/** Twelve complete weeks — long enough to smooth the weekly digest cadence, short enough to stay
 * seasonal. Both fraud euros and traffic counts move with the season, so a year mean would lie. */
export const COUNTER_RATE_WEEKS = 12;

/** Euros carry integer cents on the wire and are shown whole; counts are whole incidents. */
export type CounterFlavour = 'euro' | 'count';

/** A figure and the last day it actually covers — `Stats.last_date`, never `Date.now()`. */
export interface CounterBaseline {
  value: number;
  asOf: string | null;
}

export type CounterState =
  | { kind: 'pending' }
  | { kind: 'empty' }
  | { kind: 'live'; asOf: string; value: number; perDay: number }
  | { kind: 'frozen'; asOf: string; value: number; reason: FreezeReason };

export interface CounterInput {
  /** Whether the figures are still in flight. Distinguishes "not answered yet" — which draws a
   * skeleton — from "answered, and the window holds nothing", which is a measurement. */
  pending: boolean;
  baseline: CounterBaseline;
  /** The trailing mean from {@link counterRate}, or `null` when no week has closed. */
  perDay: number | null;
  coverage: CoverageFact;
  /** Today in Tallinn, from `todayInTallinn`, so the lag is measured on the corpus' calendar. */
  today: string;
}

function daysBetween(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / MS_PER_DAY);
}

const TALLINN_CLOCK = new Intl.DateTimeFormat('sv-SE', {
  timeZone: TALLINN,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
});

/** The instant a Tallinn calendar day ends. Anchoring on UTC midnight instead would hold the
 * counter still until 03:00 local and then jump — the days here are Estonian days, not UTC ones. */
export function endOfTallinnDay(ymd: string): number {
  const utcNext = Date.parse(`${ymd}T00:00:00Z`) + MS_PER_DAY;
  const wall = Date.parse(`${TALLINN_CLOCK.format(new Date(utcNext)).replace(' ', 'T')}Z`);
  return utcNext - (wall - utcNext);
}

/** The trailing mean daily rate from a **weekly** series. Only closed weeks count — the running one
 * is short — and `null` says none has closed, which the caller must freeze on rather than guess. */
export function counterRate(
  points: readonly SeriesPoint[],
  metric: SeriesMetricKey,
  asOf: string,
  from: string,
): number | null {
  // A bucket whose Monday falls before the window holds only the days the window asked for, so it
  // is a short week wearing a full week's label — counting it measures the rate low every January.
  const closed = points
    .filter((p) => p.bucket >= from && daysBetween(p.bucket, asOf) >= 6)
    .slice(-COUNTER_RATE_WEEKS);
  if (closed.length === 0) return null;
  const total = closed.reduce((sum, p) => sum + p[metric], 0);
  return total / (closed.length * 7);
}

/** Whether this counter may tick. The gap test asks only about what the extrapolation runs over: a
 * run beginning after `as_of` means the pipeline read *through* that stretch, so those days are
 * known-unread rather than not-yet-reached. The ordinary tail belongs to the lag rule alone. */
export function counterState(input: CounterInput): CounterState {
  const { pending, baseline, perDay, coverage, today } = input;
  if (pending || coverage === undefined) return { kind: 'pending' };

  const asOf = baseline.asOf;
  if (asOf === null) return { kind: 'empty' };

  const frozen = (reason: FreezeReason): CounterState =>
    ({ kind: 'frozen', asOf, value: baseline.value, reason }) as const;

  if (daysBetween(asOf, today) > COUNTER_LAG_DAYS) return frozen('lag');
  if ((coverage.extracted ?? []).some((run) => run.from > asOf)) return frozen('gap');
  if (perDay === null || perDay <= 0) return frozen('rate');

  return { kind: 'live', asOf, value: baseline.value, perDay };
}

/** `V(t) = B + r × days since the end of the day the baseline covers`, so the figure reaches the
 * extrapolated end-of-today value at midnight. Frozen and empty counters ignore the clock. */
export function counterValue(state: CounterState, now: number): number {
  if (state.kind === 'pending' || state.kind === 'empty') return 0;
  if (state.kind === 'frozen') return state.value;
  const elapsed = (now - endOfTallinnDay(state.asOf)) / MS_PER_DAY;
  return state.value + state.perDay * Math.max(0, elapsed);
}

/** What the reader is shown: whole euros (cents suppressed, never rounded up into money nobody
 * reported) or a whole incident. Counts bump irregularly, which reads more honestly than a crawl. */
export function quantiseCounter(value: number, flavour: CounterFlavour): number {
  return flavour === 'euro' ? Math.floor(value / 100) * 100 : Math.floor(value);
}

/** The monotonic clamp: a stale-while-revalidate body arriving after a fresh one must not roll the
 * figure backwards, because a corpus cannot take incidents away. */
export function clampMonotonic(previous: number | null, candidate: number): number {
  return previous === null ? candidate : Math.max(previous, candidate);
}

/** True when the reader has asked the operating system for less motion. SSR-safe: no window, no
 * animation, which is also the right answer for the server-rendered tests. */
function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true
  );
}

export interface LiveCounter {
  state: CounterState;
  /** The figure to render, already quantised and monotonically clamped. */
  shown: number;
  /** Whether the mount count-up may play — never for a frozen counter, never under reduced motion. */
  animates: boolean;
}

/** The ticking figure, recomputed per frame while live. `requestAnimationFrame` rather than an
 * interval: a hidden tab pauses it, so a backgrounded counter re-anchors instead of catching up. */
export function useLiveCounter(input: CounterInput, flavour: CounterFlavour): LiveCounter {
  const { pending, baseline, perDay, coverage, today } = input;
  const { value, asOf } = baseline;
  // Memoised on what the figure is made of, because `input` is a fresh object on every render and
  // the frame loop below keys on the state: without this it tore itself down and re-armed per frame.
  const state = useMemo(
    () => counterState({ pending, baseline: { value, asOf }, perDay, coverage, today }),
    [pending, value, asOf, perDay, coverage, today],
  );
  const still = state.kind !== 'live' || prefersReducedMotion();
  const highest = useRef<number | null>(null);
  // The clamp absorbs a stale refetch *within* one live run: a stopped counter must show the figure
  // it stands at, not the high water mark it reached while guessing.
  const anchor = state.kind === 'live' ? state.asOf : null;
  const anchored = useRef<string | null>(anchor);
  if (anchored.current !== anchor) {
    anchored.current = anchor;
    highest.current = null;
  }
  const [shown, setShown] = useState(() =>
    clampMonotonic(highest.current, quantiseCounter(counterValue(state, Date.now()), flavour)),
  );

  useEffect(() => {
    const tick = (): void => {
      const next = clampMonotonic(
        highest.current,
        quantiseCounter(counterValue(state, Date.now()), flavour),
      );
      highest.current = next;
      setShown(next);
    };
    tick();
    if (still) return;
    let frame = requestAnimationFrame(function loop() {
      tick();
      frame = requestAnimationFrame(loop);
    });
    return () => cancelAnimationFrame(frame);
  }, [state, flavour, still]);

  return { state, shown, animates: !still };
}
